import { useEffect, useState } from 'react'
import { datadogLogs, LogsEvent } from '@datadog/browser-logs'
import { useRoles, useUserProfile } from 'core/hooks/data/useDataHook'
import useCurrentTeam from 'core/hooks/team/useCurrentTeam'
import { useSettings } from 'core/hooks/settings/useSettings'

const IGNORED_MESSAGE_PATTERNS = [
  /AxiosError/,
  /Ada Embed has already been started\./,
  /https:\/\/px\.ads\.linkedin\.com\/wa\//,
  /https:\/\/[^/]*\.braze\.com\/api\//,
  /https:\/\/api-js\.mixpanel\.com\//,
  /https:\/\/[^/]*\.clarity\.ms\//
]

const shouldIgnoreLog = (message: string) =>
  IGNORED_MESSAGE_PATTERNS.some((pattern) => pattern.test(message))

const beforeSend = (event: LogsEvent) => {
  return !shouldIgnoreLog(event.message)
}

const useInitDataDog = () => {
  const { data: user } = useUserProfile()
  const { data: userRoles } = useRoles()
  const { data: currentTeam } = useCurrentTeam()
  const { data: settings } = useSettings()
  const isA2 = settings?.featureFlags?.ENABLE_DRIVER_EXPERIENCE === true
  const [hasClearedUser, setHasClearedUser] = useState(false)

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      datadogLogs.init({
        clientToken: import.meta.env.VITE_APP_DATADOG_CLIENT_TOKEN_KEY,
        site: 'datadoghq.com',
        forwardErrorsToLogs: true,
        sessionSampleRate: 100,
        service: 'everlance-dashboard',
        beforeSend
      })
    }
  }, [])

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return

    // Keyed off `id`: A2 users have no `email` in their context.
    const hasUser = !!datadogLogs.getUser()?.id
    if (user?.token_id && !hasUser) {
      // A2 users: no PII (email, roles) in logs (ACTION-007 / THREAT-011).
      datadogLogs.setUser(
        isA2
          ? { id: user.token_id as string, teamId: currentTeam?.token_id }
          : {
              id: user.token_id as string,
              email: user.email,
              roles: userRoles?.map((role) => role.name),
              teamId: currentTeam?.token_id
            }
      )
    }

    if (!user?.token_id && hasUser && !hasClearedUser) {
      datadogLogs.clearUser()
      setHasClearedUser(true)
    }
  }, [
    currentTeam?.token_id,
    hasClearedUser,
    isA2,
    user?.email,
    user?.token_id,
    userRoles
  ])

  return null
}

export default useInitDataDog
